/**
 * InstrumentHeader component.
 * Displays day label, state, step info, and attempt dial.
 */

import { DayLabel, MAX_ATTEMPTS } from '@daily-puzzle/shared';
import './InstrumentHeader.css';

export interface InstrumentHeaderProps {
    /** Day label (TODAY or YESTERDAY) */
    dayLabel: DayLabel;
    /** Which color is to move */
    attacker: 'w' | 'b';
    /** Current step (1-indexed for display) */
    currentStep: number;
    /** Total steps in puzzle */
    totalSteps: number;
    /** Attempts used */
    attemptsUsed: number;
    /** Puzzle status */
    status: 'in_progress' | 'solved' | 'missed' | 'revealed';
}

export function InstrumentHeader({
    dayLabel,
    attacker,
    currentStep,
    totalSteps,
    attemptsUsed,
    status,
}: InstrumentHeaderProps) {
    const colorText = attacker === 'w' ? 'White to move' : 'Black to move';
    const isFinished = status !== 'in_progress';

    return (
        <header className="instrument-header">
            <div className="header-left">
                <span className="day-label">{dayLabel}</span>
                <span className="state-line">{colorText}</span>
                {!isFinished && (
                    <span className="sub-line">
                        Your move • Step {currentStep + 1} of {totalSteps}
                    </span>
                )}
                {isFinished && (
                    <span className="sub-line sub-line--finished">
                        {status === 'solved' && 'Puzzle solved!'}
                        {status === 'missed' && 'Better luck tomorrow'}
                        {status === 'revealed' && 'Solution revealed'}
                    </span>
                )}
            </div>

            <div className="header-right">
                <AttemptDial attemptsUsed={attemptsUsed} maxAttempts={MAX_ATTEMPTS} />
            </div>
        </header>
    );
}

interface AttemptDialProps {
    attemptsUsed: number;
    maxAttempts: number;
}

function AttemptDial({ attemptsUsed, maxAttempts }: AttemptDialProps) {
    const ticks = [];

    for (let i = 0; i < maxAttempts; i++) {
        const isUsed = i < attemptsUsed;
        ticks.push(
            <div
                key={i}
                className={`dial-tick ${isUsed ? 'dial-tick--used' : ''}`}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            className="attempt-dial"
            role="img"
            aria-label={`${attemptsUsed} of ${maxAttempts} attempts used`}
        >
            <div className="dial-ring">
                {ticks}
            </div>
            <span className="dial-label">{attemptsUsed}/{maxAttempts}</span>
        </div>
    );
}
